/**
 * Form — uniwersalne prymitywy formularzy dla React-CMS.
 *
 * **Tokeny brand:** wszystkie style pochodzą z CSS variables w `app/globals.css`
 * — komponenty nie zawierają hardcoded kolorów ani odstępów. Zmiana template'a
 * (`data-template="dark_brand"`) automatycznie przemalowuje wszystkie pola.
 *
 * **Stany (każdy control):** default / hover / focus / focus-visible / invalid
 * / success / disabled / readonly + placeholder. Pełen pakiet UX + WCAG 2.1 AA
 * kontrasty.
 *
 * **A11y:**
 *   - `<Field>` generuje `id`/`htmlFor`/`aria-describedby`/`aria-invalid`/
 *     `aria-required` automatycznie przez React Context
 *   - `<FormStatus variant="error">` ma `role="alert"`, `variant="success"`
 *     ma `role="status"` (zgodnie z severity)
 *   - Checkbox/Radio całością klikalne (label-wrap)
 *   - `focus-visible` ring tylko dla nawigacji klawiaturą (mysz: sam border)
 *
 * **Przykład minimalny:**
 *
 * ```tsx
 * import { Field, Input, FormStatus } from "@/components/ui/Form";
 *
 * <form onSubmit={...}>
 *   <Field label="Email" required error={errors.email} hint="Wyślemy potwierdzenie">
 *     <Input type="email" value={email} onChange={...} autoComplete="email" />
 *   </Field>
 *   {topError && <FormStatus variant="error">{topError}</FormStatus>}
 * </form>
 * ```
 *
 * @see Form.module.css — tokens, state variants, addon slots
 * @see Field.tsx — context provider dla auto-id i aria binding
 */

export { Field, useFieldContext } from "./Field";
export type { FieldProps } from "./Field";

export { Input } from "./Input";
export type { InputProps } from "./Input";

export { PhoneInput } from "./PhoneInput";
export type { PhoneInputProps } from "./PhoneInput";

export { Textarea } from "./Textarea";
export type { TextareaProps } from "./Textarea";

export { FileUpload } from "./FileUpload";
export type { FileUploadProps } from "./FileUpload";

export { Select } from "./Select";
export type { SelectProps } from "./Select";

export { Checkbox } from "./Checkbox";
export type { CheckboxProps } from "./Checkbox";

export { Radio } from "./Radio";
export type { RadioProps } from "./Radio";

export { RangeSlider } from "./RangeSlider";
export type { RangeSliderProps } from "./RangeSlider";

export { FormStatus } from "./FormStatus";
export type { FormStatusProps, FormStatusVariant } from "./FormStatus";

export { Honeypot } from "./Honeypot";
export type { HoneypotProps } from "./Honeypot";

export { SubmitButton } from "./SubmitButton";
export type { SubmitButtonProps } from "./SubmitButton";

export {
  PasswordStrengthMeter,
  buildPasswordRegex,
} from "./PasswordStrengthMeter";
export type {
  PasswordStrengthMeterProps,
  PasswordStrengthLabels,
  PasswordRequirementsInput,
} from "./PasswordStrengthMeter";
